import { ConfigService, Constants } from '@packages/core/config';
import { getLogger } from '@packages/core/logging';

import type { RuntimeContext } from '../runtime';

const logger = getLogger('feature-flags');

/**
 * Default Unleash Frontend API URL, used when no override is configured.
 */
const DEFAULT_UNLEASH_URL = 'https://us.app.unleash-hosted.com/usoo0099/api/frontend';

/**
 * Returns `true` when the host is a development context where the toolbar should be shown.
 *
 * @param host - the resolved host value
 *
 * @returns `true` for local, development, or preview hosts
 */
export function isToolbarEnabled(host: string): boolean {
    return host === 'local' || host === 'development' || host === 'preview';
}

/**
 * Builds the Unleash SDK configuration object from ConfigService values and runtime context.
 * Context fields are NOT included here; they are synced separately by the sync hook.
 *
 * @param configService - the ConfigService instance
 *
 * @returns the SDK config object, or `undefined` if no client key is configured
 */
export function buildUnleashConfig(configService: ConfigService) {
    const clientKey = configService.get(Constants.Env.UnleashClientKey);

    if (!clientKey) {
        logger.warn('Unleash client key is not configured; feature flags will be unavailable');
        return undefined;
    }

    const url = configService.get(Constants.Env.UnleashUrl) ?? DEFAULT_UNLEASH_URL;

    return {
        appName: 'techshare',
        clientKey,
        refreshInterval: 15,
        url,
    };
}

type ToolbarInstance = {
    /** Returns the current toolbar state including any developer overrides. */
    getState: () => { contextOverrides: Record<string, unknown> };

    /** Merges the given fields into the toolbar's context overrides. */
    setContextOverride: (ctx: Record<string, unknown>) => void;

    /** The internal state manager; used to subscribe to reset events. */
    stateManager: {
        subscribe: (listener: (event: { type: string }) => void) => () => void;
    };
};

type ToolbarClient = {
    __toolbar?: ToolbarInstance;
};

/**
 * Extracts the toolbar instance from an Unleash client, if present.
 *
 * @param unleashClient - the Unleash client (may be wrapped with toolbar)
 *
 * @returns the toolbar instance, or `undefined`
 */
export function getToolbar(unleashClient: unknown): ToolbarInstance | undefined {
    return (unleashClient as ToolbarClient).__toolbar;
}

/**
 * Filters context fields that the developer has NOT manually overridden in the toolbar.
 * Prevents programmatic context updates from clobbering developer overrides on refresh.
 *
 * @param context - the context fields to set
 * @param existingOverrides - the toolbar's current override state
 *
 * @returns a new object containing only non-overridden fields, or `null` if everything was filtered
 */
export function filterOverriddenFields(
    context: Record<string, unknown>,
    existingOverrides: Record<string, unknown>
): Record<string, unknown> | null {
    const filtered: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(context)) {
        if (key === 'properties') {
            const existingProps = (existingOverrides.properties ?? {}) as Record<string, unknown>;
            const newProps = value as Record<string, unknown>;
            const filteredProps: Record<string, unknown> = {};

            for (const [propKey, propValue] of Object.entries(newProps)) {
                if (!(propKey in existingProps)) {
                    filteredProps[propKey] = propValue;
                }
            }

            if (Object.keys(filteredProps).length > 0) {
                filtered.properties = filteredProps;
            }
        } else if (!(key in existingOverrides)) {
            filtered[key] = value;
        }
    }

    return Object.keys(filtered).length > 0 ? filtered : null;
}

/**
 * Syncs a context object to both the SDK (via updateContext callback) and the toolbar UI.
 * Respects developer overrides in the toolbar.
 *
 * @param unleashClient - the Unleash client instance
 * @param updateContext - callback that updates the SDK context
 * @param context - the context fields to set
 */
export function syncContextToToolbar(
    unleashClient: unknown,
    updateContext: (ctx: Record<string, unknown>) => void,
    context: Record<string, unknown>
): void {
    updateContext(context);

    const toolbar = getToolbar(unleashClient);

    if (!toolbar) {
        return;
    }

    // Preserve sessionId in the toolbar override. The toolbar captures originalBaseContext
    // at init, which may be before the SDK generates sessionId asynchronously. Without this,
    // the toolbar display loses sessionId after we set overrides.
    const client = unleashClient as { getContext?: () => Record<string, unknown> };
    const currentContext = client.getContext?.() ?? {};
    const enriched = { ...context };

    if (currentContext.sessionId && !enriched.sessionId) {
        enriched.sessionId = currentContext.sessionId;
    }

    const filtered = filterOverriddenFields(enriched, toolbar.getState().contextOverrides);

    if (filtered) {
        toolbar.setContextOverride(filtered);
    }
}

/**
 * Converts a RuntimeContext object into the shape expected by the Unleash SDK context.
 * Standard fields (userId, environment) go at the top level; custom fields go under properties.
 *
 * @param runtimeContext - the full runtime context
 *
 * @returns the context object shaped for the Unleash SDK
 */
export function buildUnleashContext(runtimeContext: RuntimeContext): Record<string, unknown> {
    // Note: appName and environment are static (set at SDK init) and cannot be updated
    // via updateContext. Passing them triggers a console warning from the SDK.
    return {
        userId: runtimeContext.userId,
        properties: {
            backend: runtimeContext.backend,
            environment: runtimeContext.environment,
            emailAddress: runtimeContext.emailAddress,
            host: runtimeContext.host,
            platform: runtimeContext.platform,
            username: runtimeContext.username,
            userRoles: runtimeContext.userRoles,
        },
    };
}

export type { ToolbarInstance };
